import { get, set, del, keys } from "idb-keyval";

const CACHE_PREFIX = "stream-cache:";
const SEVEN_DAYS_MS = 1000 * 60 * 60 * 24 * 7;
const supportsPersistentCache = typeof indexedDB !== "undefined";

// In-memory cache store
const memoryCache = new Map<string, CachedData>();

type CacheMetadata = {
    lastUpdated: number;
    lastOffset: number;
};

type CachedData = {
    chunk: Uint8Array;
    metadata: CacheMetadata;
};

// Track if cache has changed since last persistence
let cacheChanged = false;
let persistInProgress = false;

// Drop all stale data from IndexedDB
async function dropStaleData() {
    if (!supportsPersistentCache) {
        return;
    }
    try {
        const allKeys = await keys(); // Enumerate all keys in IndexedDB
        for (const k of allKeys) {
            if (typeof k === "string" && k.startsWith(CACHE_PREFIX)) {
                const data = await get<CachedData>(k);
                if (!data) {
                    continue;
                }
                const isStale = Date.now() - data.metadata.lastUpdated > SEVEN_DAYS_MS;
                if (isStale) {
                    await del(k);
                }
            }
        }
    } catch (error) {
        console.warn("Failed to drop stale data:", error);
    }
}

// Save memory cache to IndexedDB
async function persistMemoryCache() {
    if (!supportsPersistentCache || !cacheChanged || persistInProgress) {
        return;
    }
    cacheChanged = false;
    persistInProgress = true;
    try {
        const promises: Promise<void>[] = [];
        for (const [key, data] of memoryCache.entries()) {
            promises.push(set(key, data));
        }
        await Promise.all(promises);
    } catch (error) {
        console.warn("Failed to persist memory cache:", error);
        cacheChanged = true; // try again next time
    } finally {
        persistInProgress = false;
    }
}

// Setup event listeners for cache persistence
function setupCachePersistence() {
    // Save cache when page visibility changes
    document.addEventListener(
        "visibilitychange",
        () => {
            if (document.visibilityState === "hidden") {
                void persistMemoryCache();
            }
        },
        { capture: true },
    );

    // Save cache before window unload
    window.addEventListener(
        "beforeunload",
        () => {
            void persistMemoryCache();
        },
        { capture: true },
    );

    // Throttled periodic save (only if changes exist)
    setInterval(
        () => {
            void persistMemoryCache();
        },
        1000 * 60 * 5,
    );
}

// Initialize the cache system.
// Skip persistence setup in test environments — the event listeners and
// setInterval keep vitest's threads pool alive indefinitely.
const isTestEnv = import.meta.env.MODE === "test";
const ready = dropStaleData().then(() => {
    if (!isTestEnv) {
        setupCachePersistence();
    }
});

/**
 * Creates a cache key for a given stream identifier
 */
function createCacheKey(key: string): string {
    return `${CACHE_PREFIX}${key}`;
}

/**
 * Loads cached data from memory first, then IndexedDB as fallback
 */
export async function loadFromCache(key: string): Promise<CachedData | null> {
    await ready;
    const cacheKey = createCacheKey(key);

    if (!memoryCache.has(cacheKey)) {
        if (!supportsPersistentCache) {
            return null;
        }
        try {
            const data = await get<CachedData>(cacheKey);
            if (data) {
                memoryCache.set(cacheKey, data);
            }
        } catch (error) {
            console.warn("Failed to load from cache:", error);
        }
    }

    // Check memory cache first
    const memData = memoryCache.get(cacheKey);
    if (memData) {
        const isStale = Date.now() - memData.metadata.lastUpdated > SEVEN_DAYS_MS;
        if (!isStale) {
            return memData;
        }
        memoryCache.delete(cacheKey);
        await evictFromCache(key);
        return null;
    }

    return null;
}

/**
 * Saves data to memory cache
 *
 * Exported for testing purposes only
 */
export async function saveToCache(key: string, chunk: Uint8Array, offset: number): Promise<void> {
    await ready;
    const cacheKey = createCacheKey(key);
    const data: CachedData = {
        chunk,
        metadata: {
            lastUpdated: Date.now(),
            lastOffset: offset,
        },
    };

    memoryCache.set(cacheKey, data);
    cacheChanged = true;
}

/**
 * Removes data from both memory and persistent cache
 */
export async function evictFromCache(key: string): Promise<void> {
    await ready;
    const cacheKey = createCacheKey(key);
    memoryCache.delete(cacheKey);
    if (!supportsPersistentCache) {
        return;
    }
    try {
        await del(cacheKey);
    } catch (error) {
        console.warn("Failed to evict from cache:", error);
    }
}

/**
 * Fetches a stream with caching support, following fetch-like signature
 * @param key Cache key for the request
 * @param input Request info (URL or Request object)
 * @param init Optional request init options
 * @returns ReadableStream of Uint8Array chunks
 */
export function streamWithCache(key: string, input: RequestInfo | URL, init?: RequestInit): ReadableStream<Uint8Array> {
    const { readable, writable } = new TransformStream<Uint8Array, Uint8Array>();
    let fetchOffset = 0;
    let cachedOffset = 0;

    // Start the streaming process
    void (async () => {
        const writer = writable.getWriter();

        try {
            // Serve cached data first
            const cacheData = await loadFromCache(key);
            if (cacheData) {
                cachedOffset = cacheData.metadata.lastOffset;

                // If the abort signal was triggered already we need to close the stream and return early.
                if (init?.signal?.aborted) {
                    console.log("Loaded stream from cache, but abort signal was triggered so returning early");
                    await writer.close();
                    return;
                }

                await writer.write(cacheData.chunk);
            }

            // Fetch new data
            const response = await fetch(input, init);
            if (!response.ok || !response.body) {
                throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
            }

            let accumulatedChunk = new Uint8Array(0);
            const reader = response.body.getReader();

            while (!init?.signal?.aborted) {
                const { done, value } = await reader.read();
                if (done) break;

                // Update fetch offset
                fetchOffset += value.length;

                // Merge the new chunk with accumulated data
                const newAccumulated = new Uint8Array(accumulatedChunk.length + value.length);
                newAccumulated.set(accumulatedChunk);
                newAccumulated.set(value, accumulatedChunk.length);
                accumulatedChunk = newAccumulated;

                // Always update the cache with the latest accumulated chunk
                const pending: Promise<void>[] = [];
                pending.push(saveToCache(key, accumulatedChunk, fetchOffset));

                // Only write to stream if the chunk is beyond cached data
                if (fetchOffset > cachedOffset) {
                    // If we have some overlap, slice off the overlapping part
                    const streamChunk =
                        fetchOffset - value.length < cachedOffset
                            ? value.slice(cachedOffset - (fetchOffset - value.length))
                            : value;

                    if (streamChunk.length > 0) {
                        pending.push(writer.write(streamChunk));
                    }
                }
                await Promise.all(pending);
            }
            await writer.close();
        } catch (error) {
            await writer.abort(error);
        }
    })();

    return readable;
}
